/**
 * User theme preference (docs/15 §5, §10 deviation 3).
 *
 * The apps ship **light** as the default and stay there until the operator
 * flips the switch; the choice is remembered per browser in `localStorage` and
 * applied as `data-theme` on `<html>`, the same attribute the token blocks in
 * `tokens.css` key off. `prefers-color-scheme` is deliberately NOT consulted —
 * docs/15 §5 wants a predictable light default for dense admin work, so the OS
 * setting never silently darkens the app; the toggle is the only input.
 *
 * This module is the base layer. {@link ThemeScope} still overrides the root
 * theme for the duration of a route (the login hero), and restores whatever the
 * preference had put there on unmount.
 */

export type Theme = 'light' | 'dark';

/** Also read by the inline boot script in each app's `index.html` — keep in sync. */
export const THEME_STORAGE_KEY = 'bia.theme';

export const DEFAULT_THEME: Theme = 'light';

function isTheme(value: unknown): value is Theme {
  return value === 'light' || value === 'dark';
}

/**
 * The stored preference, or `null` when nothing valid is stored. Storage access
 * throws in a few browser configurations (Safari private mode, cookies blocked)
 * and this runs during boot, so a failure degrades to the default rather than
 * taking the whole app down with it.
 */
function readStoredTheme(): Theme | null {
  try {
    const raw = localStorage.getItem(THEME_STORAGE_KEY);
    return isTheme(raw) ? raw : null;
  } catch {
    return null;
  }
}

let current: Theme = readStoredTheme() ?? DEFAULT_THEME;

const listeners = new Set<() => void>();

/**
 * Active {@link ThemeScope} pins, innermost last. This module is the **only**
 * writer of `documentElement.dataset.theme`: a scope that painted the root
 * itself would fight the preference — a cross-tab flip would repaint the pinned
 * login hero, and the scope's unmount would restore a DOM value snapshotted
 * before that flip, leaving the app disagreeing with `localStorage` until a
 * reload. Routing both through `applyResolvedTheme` keeps one source of truth.
 */
const scopes: { theme: Theme }[] = [];

/** What `<html>` should show: the innermost pin if any, else the preference. */
function resolvedTheme(): Theme {
  return scopes.length > 0 ? scopes[scopes.length - 1]!.theme : current;
}

function applyResolvedTheme(): void {
  document.documentElement.dataset['theme'] = resolvedTheme();
}

/**
 * Pin the root theme regardless of the preference, and return the release
 * function. Backs {@link ThemeScope} — call it from there, not directly.
 * Releasing re-resolves, so the root lands on the preference **as it stands
 * now**, not as it stood when the pin was taken.
 */
export function pushThemeScope(theme: Theme): () => void {
  const entry = { theme };
  scopes.push(entry);
  applyResolvedTheme();
  return () => {
    const at = scopes.indexOf(entry);
    if (at !== -1) scopes.splice(at, 1);
    applyResolvedTheme();
  };
}

/** The preference in effect — not necessarily what `<html>` shows under a `ThemeScope`. */
export function getTheme(): Theme {
  return current;
}

/**
 * Persist and apply a choice, then wake every `useTheme` subscriber. While a
 * scope is pinned the root keeps the pinned theme; the preference still moves,
 * and surfaces the moment the scope releases.
 */
export function setTheme(theme: Theme): void {
  current = theme;
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // Preference can't be remembered here; the in-memory switch below still works.
  }
  applyResolvedTheme();
  for (const listener of listeners) listener();
}

export function toggleTheme(): void {
  setTheme(current === 'dark' ? 'light' : 'dark');
}

export function subscribeTheme(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** A flip in another tab of the same app — mirror it here. */
function onStorage(event: StorageEvent): void {
  if (event.key !== THEME_STORAGE_KEY) return;
  const next = isTheme(event.newValue) ? event.newValue : DEFAULT_THEME;
  if (next === current) return;
  current = next;
  applyResolvedTheme();
  for (const listener of listeners) listener();
}

/**
 * Apply the stored preference at boot and keep other tabs in step. Call once
 * from the app entry, before the first render, so no route ever mounts against
 * the wrong token set. The `storage` event only fires in the *other* tabs, so
 * this is what makes a flip in one admin tab reach the rest. Idempotent — the
 * listener is swapped, not stacked, if this is ever called twice.
 */
export function initTheme(): Theme {
  applyResolvedTheme();
  window.removeEventListener('storage', onStorage);
  window.addEventListener('storage', onStorage);
  return current;
}

/** Test seam: drop the in-memory state, pins and subscribers back to a clean boot. */
export function resetThemeForTests(): void {
  current = readStoredTheme() ?? DEFAULT_THEME;
  listeners.clear();
  scopes.length = 0;
  window.removeEventListener('storage', onStorage);
}
